/**
 * TelemetryDeck analytics — fires in both dev and production.
 *
 * Dev signals go to the "BHG Dev" app (VITE_TELEMETRY_APP_ID in .env.local).
 * Production signals go to the "BHG Production" app (set in AWS Amplify).
 *
 * If VITE_TELEMETRY_APP_ID is not set, every call is a no-op — safe to use
 * in any environment without wrapping call sites in guards.
 *
 * The npm JavaScript SDK does not auto-collect device or app metadata (unlike
 * the script-tag Web SDK). We attach app version, platform, browser, and
 * device type on every signal so Metrics → Devices / Versions populate.
 */

import TelemetryDeck from '@telemetrydeck/sdk';

import { getOrCreateAnonymousTelemetryClientUser } from '@utils/telemetryClientUser';
import { getTelemetryEnvironmentPayload } from '@utils/telemetryDevice';

let instance: TelemetryDeck | null = null;

function mergePayload(payload?: Record<string, string>): Record<string, string> {
  return {
    ...getTelemetryEnvironmentPayload(),
    ...payload,
  };
}

/**
 * Call once at app startup (before React renders).
 * Creates the singleton TelemetryDeck instance.
 */
export function initTelemetry(): void {
  const appID = import.meta.env.VITE_TELEMETRY_APP_ID;
  if (!appID) {
    return;
  }

  instance = new TelemetryDeck({
    appID,
    clientUser: getOrCreateAnonymousTelemetryClientUser(),
  });
}

/**
 * Sends a signal to TelemetryDeck.
 *
 * @param signalType - Use a constant from EVENTS in @constants/telemetryEvents
 * @param payload    - Optional key/value metadata (all values must be strings)
 *
 * Errors are swallowed — analytics must never break the app.
 */
export async function signal(
  signalType: string,
  payload?: Record<string, string>
): Promise<void> {
  if (!instance) {
    return;
  }
  try {
    await instance.signal(signalType, mergePayload(payload));
  } catch {
    // intentionally silent
  }
}
